import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * Modal for å opprette et nytt creator-album.
 * Profil-separasjon (Iter 47): bilder/video som lastes opp i dette albumet
 * vises KUN på /skaper/{handle}/galleri, ikke på vanlig profil.
 */
export default function CreatorAlbumModal({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [newAlbum, setNewAlbum] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 2) {
      toast.error("Tittel må være minst 2 tegn");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/albums", {
        title: title.trim(),
        description: description.trim(),
        is_nsfw: true,
        owner_pool: "creator",
      });
      setNewAlbum(r.data);
      toast.success("Album opprettet. Last opp bilder og video.");
      if (onCreated) onCreated(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 pt-24 sm:pt-4 items-start sm:items-center" data-testid="creator-album-modal">
      <div className="w-full max-w-md bg-[var(--noir-elev)] border border-white/10 p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--cream-faint)] hover:text-[var(--cream)]"
          data-testid="creator-album-close-btn"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">Nytt skaper-album</h2>
        <p className="text-[var(--cream-soft)] text-sm mb-5">
          Innhold du legger her vises <span className="text-[var(--magenta)]">kun på skapersiden din</span>, ikke på din vanlige profil.
        </p>

        {newAlbum ? (
          <div className="space-y-4" data-testid="creator-album-success">
            <div className="bg-[var(--magenta)]/10 border border-[var(--magenta)]/30 p-4 text-[var(--cream)]">
              Albumet «{newAlbum.title}» er opprettet. Trykk under for å laste opp bilder og video.
            </div>
            <Link
              to={`/albums/${newAlbum.id}`}
              className="block text-center py-3 bg-[var(--magenta)] text-white font-meta tracking-[0.15em] hover:opacity-90"
              data-testid="creator-album-open-link"
            >
              Åpne album →
            </Link>
            <button
              onClick={() => { setNewAlbum(null); setTitle(""); setDescription(""); }}
              className="w-full text-center py-2 text-[var(--cream-soft)] text-sm hover:text-[var(--cream)]"
            >
              Lag enda et album
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block font-meta text-xs uppercase tracking-[0.2em] text-[var(--cream-soft)] mb-1.5">
                Tittel
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="f.eks. Studio-økt mai 2026"
                className="w-full h-10 px-3 bg-[var(--noir)] border border-white/10 text-[var(--cream)] text-sm"
                required
                minLength={2}
                maxLength={80}
                data-testid="creator-album-title-input"
              />
            </div>

            <div>
              <label className="block font-meta text-xs uppercase tracking-[0.2em] text-[var(--cream-soft)] mb-1.5">
                Beskrivelse (valgfritt)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Kort tekst som vises på albumet."
                className="w-full p-3 bg-[var(--noir)] border border-white/10 text-[var(--cream)] text-sm"
                data-testid="creator-album-description-input"
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              className="w-full py-3 bg-[var(--magenta)] text-white font-meta tracking-[0.15em] hover:opacity-90 disabled:opacity-40 inline-flex items-center justify-center gap-2"
              data-testid="creator-album-submit-btn"
            >
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Oppretter…</> : "Opprett album"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
